import type { EnvironmentProviders } from '@angular/core';

import { isPlatformBrowser } from '@angular/common';
import { computed, effect, inject, Injectable, PLATFORM_ID, provideEnvironmentInitializer } from '@angular/core';
import { KameleoonCustomDataIndexEnum } from '@core/services/kameleoon/kameleoon-custom-data.enum';
import { KameleoonSdkService } from '@core/services/kameleoon/kameleoon-sdk.service';
import { environment } from '@environment';
import { Store } from '@ngrx/store';
import { BankingStore } from '@stores/banking/banking.store';
import { OrdersStore } from '@stores/orders/orders-store';
import { userId as userIdSelector } from '@wizbii-utils/angular/stores';

@Injectable({ providedIn: 'root' })
export class KameleoonCustomDataService {
  readonly #isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  readonly #kameleoonSdkService = inject(KameleoonSdkService);
  readonly #bankingStore = inject(BankingStore);
  readonly #ordersStore = inject(OrdersStore);
  readonly #store = inject(Store);

  readonly #userId = this.#store.selectSignal(userIdSelector);

  readonly #assignedCcoId = computed(() => {
    const order = this.#ordersStore.order();

    return order?.client.ownerId || order?.dossiers.at(0)?.ownerId;
  });

  constructor() {
    if (this.#isBrowser) {
      this.#setRegularCustomData();
    }
  }

  setHelpsCustomData(count: number | undefined, amount: number | undefined): void {
    if (!this.#isBrowser) {
      return;
    }

    if (count && count >= 0) {
      this.#kameleoonSdkService.setCustomData(KameleoonCustomDataIndexEnum.HelpsCount, `${count}`);
    }

    if (amount && amount >= 0) {
      this.#kameleoonSdkService.setCustomData(KameleoonCustomDataIndexEnum.HelpsAmount, `${amount}`);
    }
  }

  #setRegularCustomData(): void {
    this.#kameleoonSdkService.setCustomData(KameleoonCustomDataIndexEnum.Lang, environment.i18n.lang);

    effect(() => {
      const userId = this.#userId();
      if (userId) {
        this.#kameleoonSdkService.setVisitorUserId(userId);
        this.#kameleoonSdkService.setCustomData(KameleoonCustomDataIndexEnum.VisitorId, userId);
      }
    });

    effect(() => {
      const trackInfo = this.#bankingStore.user()?.trackInfo;
      if (trackInfo) {
        this.#kameleoonSdkService.setCustomData(KameleoonCustomDataIndexEnum.PartnerId, trackInfo.partnerId);
        this.#kameleoonSdkService.setCustomData(KameleoonCustomDataIndexEnum.TrackId, trackInfo.trackId);
      }

      const partnerName = this.#bankingStore.bankingPartner()?.label;
      if (partnerName) {
        this.#kameleoonSdkService.setCustomData(KameleoonCustomDataIndexEnum.PartnerName, partnerName);
      }
    });

    effect(() => {
      const ccoId = this.#assignedCcoId();
      if (ccoId) {
        this.#kameleoonSdkService.setCustomData(KameleoonCustomDataIndexEnum.CcoId, ccoId);
      }
    });
  }
}

/** Aucun composant n'injecte ce service : il doit être instancié au bootstrap pour s'abonner. */
export function provideKameleoonCustomData(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => {
    inject(KameleoonCustomDataService);
  });
}
